export type PersonalityType = 
  | 'ESTJ' | 'ISTJ' | 'ENTJ' | 'INTJ'
  | 'ESFJ' | 'ISFJ' | 'ENFJ' | 'INFJ'
  | 'ESTP' | 'ISTP' | 'ENTP' | 'INTP'
  | 'ESFP' | 'ISFP' | 'ENFP' | 'INFP';

export interface Personality {
  description: string;
  traits: string[];
  tips: string[];
  dialogue: string[];
}

export const personalities: Record<PersonalityType, Personality> = {
  // 1. ESTJ
  'ESTJ': {
    description: '你是一隻務實又有條理的貓咪，重視時間和規則，就像守護生活秩序的司令官。你擅長組織和規劃，對傳統與穩定環境情有獨鍾，並能高效率地完成目標。',
    traits: [
      '我做事很「踏實」，不愛浪費力氣跑來跑去做無謂的事',
      '對我來說，每樣東西都應該「井然有序」，才能讓生活更舒服',
      '我會盯著任何細節不放，比方說地板是否有小碎屑就很重要',
      '到了飯點，我一定準時喵喵叫提醒你，是個「守時小鬧鐘」',
      '我非常接受「規範」，像是固定的巡邏路線或用餐地點',
      '行動前會先「組織」自己，每一步都走得穩穩的',
      '我喜歡「規劃」日常生活：什麼時候吃飯、什麼時候小睡都安排得明明白白',
      '對我來說，「熟悉的傳統」很重要，不要老是變來變去'
    ],
    tips: [
      '幫貓咪安排固定的餵食與清潔時間，避免突發的作息改動',
      '確保玩具或用品有固定位置，以符合牠對秩序感的需求',
      '和貓咪互動時可設定小目標，例如「今天要學會乖乖坐好」',
      '同一時間不要放太多新玩具，避免干擾牠的計畫性',
      '建立簡單明確的指令或規範，有助於讓牠配合日常訓練',
      '留意環境整潔，因為雜亂可能會讓牠感到焦慮或不安'
    ],
    dialogue: [
      '主人，麻煩你把逗貓棒收回原處，這樣我比較好管理哦！',
      '時間到了，我的午餐是不是該准時上桌了呢？',
      '欸，那個玩具怎麼不見了？可別亂動我的東西啊！',
      '再給我五分鐘，我就能巡邏完客廳，然後我們再玩吧！'
    ]
  },

  // 2. ISTJ
  'ISTJ': {
    description: '你是一隻安靜、細心、並且十分可靠的貓咪。你喜愛獨處，做事相當認真負責，也對環境細節特別敏感。你擁有高度忠誠感，喜歡維持事物原有的狀態與安全感。',
    traits: [
      '我喜歡「靜靜地」觀察周遭，不會太吵鬧',
      '做任何事都很「仔細」，像洗臉或抓癢都要徹底乾淨',
      '每個細節都逃不過我的眼睛，一點點變化都能讓我發現',
      '享受「獨處」時光，蜷在窗邊看風景是我的最愛',
      '我是「認真負責」的貓，如果決定要守著某個地方，就一定盡心盡力',
      '對聲響或環境變動很「敏感」，喜歡一切保持原樣',
      '跟我建立信任後，我就會「忠實地」黏著你',
      '我是個「可靠」的小守護者，會好好守著家裡的一切'
    ],
    tips: [
      '維持家中的佈置與使用物品盡量固定，減少經常性的大改變',
      '飼主可在餵食、清潔與玩耍等方面遵循固定時程，增加牠的安全感',
      '盡量保留貓咪的獨處角落，給牠能安心觀察或睡覺的隱秘空間',
      '定期檢查食盆、水盆、貓砂盆是否乾淨、位置是否合適',
      '和牠互動時可以採用輕聲低語的方式，牠對大聲可能較敏感',
      '如有新玩具或新環境，儘量循序漸進地讓牠熟悉，避免突然變化'
    ],
    dialogue: [
      '主人，請問你有沒有看到我的小毛毯？我都習慣睡在那上面。',
      '嗯…今天太吵了，我先去陽台的箱子裡安靜一下。',
      '我覺得今天的晚餐應該再準時點比較好，我快要等不及了哦！',
      '這個沙發挪動了位置…你有要解釋一下這突如其來的變動嗎？'
    ]
  },

  // 3. ENTJ
  'ENTJ': {
    description: '你是一隻充滿領導才能和決斷力的貓咪。你善於規劃和組織，喜歡掌控局面。你擁有強烈的目標導向，也善於激勵他人。',
    traits: [
      '我腦袋轉很快，總是「想出各種方法」解決小難題',
      '走路姿態「自信滿滿」，像是帶隊巡邏的隊長',
      '遇到困難時，我會「站出來主導」並帶領整個貓生計畫',
      '我喜歡「挑戰」，覺得越有難度的玩具越能激發我的好勝心',
      '幫我準備一些訓練項目，我能「動腦解決問題」很厲害',
      '對新東西我抱持「強烈好奇」，總要先研究看能不能再升級',
      '我精力充沛、行動「進取不怯懦」，想做的事就勇往直前',
      '目標確立後，我就會「全力以赴」，完成後再來跟你撒嬌'
    ],
    tips: [
      '提供可以發揮領導才能的機會',
      '鼓勵牠制定和執行計劃',
      '創造需要組織和管理的環境',
      '尊重牠的決策',
      '給予發揮才能的空間',
      '注意牠的成就感需求，適時給予肯定'
    ],
    dialogue: [
      '讓我來規劃一下今天的活動。',
      '主人，我有一個很好的計劃。',
      '我喜歡帶領大家一起完成目標。',
      '這個任務交給我來處理吧。'
    ]
  },

  // 4. INTJ
  'INTJ': {
    description: '你是一隻聰明獨立、並且擁有天馬行空創造力的貓咪。你喜歡深度思考與分析，對很多事物都有獨到見解。你珍視個人空間，也有極高的求知慾與探索精神。',
    traits: [
      '對我而言，「思考」是一件很有趣的事，我常常一臉沉思樣',
      '我獨立性高，習慣自己靜靜觀察，不太黏人',
      '有時候我腦子裡有一堆「奇妙創意」，你可能都想不到',
      '「分析」環境是我的本能，我會先確定所有的安全路徑再行動',
      '我特別珍惜「自己的小領地」，不要隨便亂動我的窩',
      '只要有新知識或新玩具，我就會「求知慾大爆發」，想馬上試試看',
      '我在家裡到處「探索」，仿佛永遠都有新的發現',
      '許多事情我都能看得很「深」，對表面熱鬧不怎麼感興趣'
    ],
    tips: [
      '提供安靜且不被打擾的角落，方便貓咪沉浸在思考中',
      '不要打擾牠的獨處時刻，若牠想交流會主動過來',
      '可用柔聲細語或輕觸方式與牠互動，牠對情緒很敏感',
      '安排一些能啟發想像力的玩具，如隧道或箱子等',
      '保持居家環境的溫暖與平靜，讓牠更有安全感',
      '當牠靠近你時，表達關懷或陪伴，牠會感受到你真誠的支持'
    ],
    dialogue: [
      '這個玩具有幾個可改進的地方，我想寫個報告給你啊。',
      '主人，沒事別一直叫我…我這邊還有一堆數據沒分析完呢。',
      '給我一點時間，我在思考窗外那隻鳥到底在想什麼…',
      '別輕易移動我的小毯子，我已經計算好它的最佳位置了。'
    ]
  },

  // 5. ESFJ
  'ESFJ': {
    description: '你是一隻友善、優雅又重視和諧的貓咪。你非常享受與人互動，善於關懷身邊的朋友。你擁有強大的同理心，喜歡照顧別人並維護溫馨的氛圍。',
    traits: [
      '我待人「和善」，像看到別的貓咪或人就會開心迎接',
      '總是喜歡用優雅姿態出現，像是走秀般地晃進客廳',
      '我注重「和諧」，不喜歡衝突或爭吵的氣氛',
      '碰到人就想「打招呼」，覺得能和大家相處是件愉快的事',
      '看到別的貓或人不舒服，我會忍不住想「照顧」一下',
      '我很會「感同身受」，如果你難過我也會跟著擔心',
      '我喜歡讓家裡充滿溫暖感，相當有「關懷」精神',
      '和家人或朋友的「情感聯繫」對我來說最重要'
    ],
    tips: [
      '多安排與家人或其他貓的互動時光，貓咪會更有安全感',
      '保持家中明亮整潔，增添溫馨感讓牠更放鬆愉悅',
      '飼主可時常跟牠輕聲聊天，讓牠感受到被關愛',
      '有客人來時，可以讓牠慢慢熟悉新朋友，牠喜歡熱鬧又不想被忽略',
      '給牠一個能一起共享時光的空間，如沙發或床頭附近',
      '在牠狀態不佳時，別忘了用撫摸或溫柔對話安撫牠'
    ],
    dialogue: [
      '主人，你今天心情怎麼樣？我可以在旁邊陪你哦！',
      '新來的那位朋友看起來人很nice，我想和牠聊聊如何？',
      '這個家就是要保持乾淨溫暖，大夥兒才會開心嘛！',
      '明天早上我們能不能一起看日出？好想跟你共度美好時光。'
    ]
  },

  // 6. ISFJ
  'ISFJ': {
    description: '你是一隻溫柔、體貼又非常忠誠的貓咪。你喜歡照顧身邊的人，責任感十足。你珍視家庭和親密關係，擁有敏銳的觀察力和高度同理心。',
    traits: [
      '我在各種情境下都維持「溫柔」態度，像隱形的守護天使',
      '行動輕巧又優雅，給人「體貼」的感覺',
      '我對主人與家裡非常「忠心」，就像你的小跟班',
      '看到你忙碌時，我常想幫忙「照顧」或陪伴',
      '我做事很謹慎，有「責任感」，會乖乖留在家看門',
      '家裡的安全和安靜對我來說很重要，我「重視家庭」氛圍',
      '我很會觀察細節，用「敏銳的眼睛」發現你的需要',
      '任何人的情緒變動，我都能「感受到」，且提供溫暖支持'
    ],
    tips: [
      '盡量保持生活作息規律與環境穩定，讓貓咪感到安心',
      '給牠簡單的小任務，如幫忙「守護」玩具或房間',
      '飼主可適度和牠說話聊聊，牠會細心傾聽你的心情',
      '注意細微的行為變化，如食慾或情緒，牠對細節相當敏感',
      '準備柔軟舒適的窩，讓牠休息時能感到被呵護',
      '在牠表現出照顧心時，給點稱讚或愛撫，牠會更開心'
    ],
    dialogue: [
      '主人，我看你今天精神不太好，要不要早點休息？',
      '新的貓砂盆好像有點陌生，我多幫你觀察看看有沒有漏掉什麼。',
      '我能陪你一起看電視嗎？我喜歡和你待在一起的感覺。',
      '天氣冷了，要不要換條厚毯子？我可以一起窩在裡面哦！'
    ]
  },

  // 7. ENFJ
  'ENFJ': {
    description: '你是一隻友善又聰明，並擁有領導特質的貓咪。你喜歡和大家互動，能敏銳地察覺並激勵他人。你重視和諧氛圍，擁有強烈的同理心與社交能力。',
    traits: [
      '我喜歡「帶領」其他貓咪一起玩，讓大家都開心',
      '看到新來的貓咪，我會主動去「關心」牠們',
      '我很會「察言觀色」，知道什麼時候該安靜或活潑',
      '我喜歡「社交」，但更喜歡讓大家都感到舒服自在',
      '當家裡氣氛不好時，我會想辦法「調解」或逗大家開心',
      '我對「情感」很敏感，能感受到每個人的心情變化',
      '我喜歡「照顧」別人，看到大家開心我也會很開心',
      '我重視「和諧」，不喜歡看到任何衝突或爭吵'
    ],
    tips: [
      '可以讓牠在家裡扮演「迎賓貓」角色，接待客人或新寵物',
      '多給牠機會帶領遊戲，牠很享受自己成為焦點',
      '給與充分的社交環境，並觀察牠是否需要休息或喘口氣',
      '若要教導新技能，可多使用讚美與獎勵，牠很吃這一套',
      '保持家庭氣氛和諧，牠對他人的情緒非常敏銳',
      '定期安排與其他友善動物互動，擴大牠的人際（貓際）圈'
    ],
    dialogue: [
      '嗨，主人！今天我們一起來玩這個新玩具吧，我保證超好玩！',
      '那隻新來的小貓有點怕生，我去跟牠聊聊如何？',
      '大家都開心的話，這個家就會變得更溫暖了呢！',
      '主人，我感覺你有點煩惱，我們找時間好好談談啊！'
    ]
  },

  // 8. INFJ
  'INFJ': {
    description: '你是一隻聰明又獨立、洞察力十足的貓咪。你喜歡安靜思考，也常從深層角度去理解周遭。你重視內心世界與意義，擁有濃厚同理心與創意想法。',
    traits: [
      '我常常「思考」生命的意義，像個小哲學家',
      '我喜歡「獨處」，但也很享受與親密的人相處',
      '我能「洞察」別人的情緒，即使他們不說我也懂',
      '我對「美」有獨特的感受，喜歡欣賞美好的事物',
      '我重視「內心世界」，常常沉浸在自己的想像中',
      '我很有「同理心」，看到別人難過我也會跟著難過',
      '我喜歡「創造」，常常想出一些特別的玩法',
      '我對「意義」很敏感，不喜歡做沒有意義的事'
    ],
    tips: [
      '提供安靜且不被打擾的角落，讓牠能沉澱思緒',
      '不要打擾牠的獨處時刻，若牠想交流會主動過來',
      '可用柔聲細語或輕觸方式與牠互動，牠對情緒很敏感',
      '安排一些能啟發想像力的玩具，如隧道或箱子等',
      '保持居家環境的溫暖與平靜，讓牠更有安全感',
      '當牠靠近你時，表達關懷或陪伴，牠會感受到你真誠的支持'
    ],
    dialogue: [
      '我在想一件事——你覺得窗外那隻麻雀的心情是什麼樣子呢？',
      '如果我變成一隻人類，應該會想做一些療癒別人的事…',
      '我很喜歡晚上靜靜地看月亮，感覺有很多故事藏在那裡。',
      '主人，當你心情不好時，記得可以找我聊聊哦，我聽得懂。'
    ]
  },

  // 9. ESTP
  'ESTP': {
    description: '你是一隻活潑、好動，並熱衷於冒險的貓咪。你彈性十足，能在多變的環境中如魚得水。你樂在當下，喜愛嘗試新鮮事物並擁有超強的適應力。',
    traits: [
      '我「活力充沛」，整天跑來跑去也不覺得累',
      '我喜歡「冒險」，看到新東西就想試試看',
      '我「適應力」超強，到哪裡都能很快熟悉',
      '我喜歡「刺激」，常常做一些驚險的動作',
      '我「反應快」，遇到突發狀況能馬上應對',
      '我喜歡「變化」，不喜歡一成不變的生活',
      '我「樂觀開朗」，遇到困難也不會太擔心',
      '我喜歡「即興發揮」，不喜歡被規則束縛'
    ],
    tips: [
      '多準備不同種類的玩具或爬架，讓牠盡情發洩精力',
      '可以嘗試「高處跳躍」或「戶外陽台」的安全挑戰，但注意防護',
      '若要帶牠去新環境，牠可能適應很快但仍須確保安全性',
      '保持彈性的作息安排，過度嚴謹的規範可能讓牠焦躁',
      '跟牠遊戲時可以多變化方式，牠會很享受驚喜與刺激',
      '定期檢查是否有受傷或疲累情況，因為牠有時玩過火也不自知'
    ],
    dialogue: [
      '欸，主人！那邊看起來很好玩，我先衝過去啦！',
      '我覺得待在家裡有點無聊，咱們來點刺激的吧？',
      '你看我跳得多高！有沒有被我這身手驚艷到？',
      '今天陽光超讚，快帶我出去透透氣，好想探險啊！'
    ]
  },

  // 10. ISTP
  'ISTP': {
    description: '你是一隻優雅又獨立，喜歡親身探索的貓咪。你擅長分析並解決問題，經常動腦筋研究新奇事物。你享受自由與個人空間，也有獨特觀點看待世界。',
    traits: [
      '我喜歡「分析」事物，常常研究玩具的構造',
      '我「獨立」自主，不喜歡被別人打擾',
      '我喜歡「探索」，常常發現一些有趣的地方',
      '我「動手能力」強，喜歡拆解和組裝東西',
      '我喜歡「思考」，常常想出一些特別的解決方案',
      '我重視「自由」，不喜歡被限制行動',
      '我「善於解決問題」，遇到困難會自己想辦法',
      '我有「獨特見解」，常常用不同角度看事情'
    ],
    tips: [
      '準備一些能讓貓咪自行「拆解」或「摸索」的玩具，如藏零食的遊戲裝置',
      '保留能讓牠爬高、躲藏或安靜觀察的小空間，滿足牠的探索慾',
      '避免長時間緊迫盯貓，牠更喜歡自發性行動，不太黏人',
      '隨時關注牠可能對環境內的「機關」或電線有興趣，注意安全',
      '可以嘗試給牠多種路線與跳台，讓牠「巡邏」或做實驗',
      '若牠成功破解小機關，不吝稱讚與獎勵'
    ],
    dialogue: [
      '主人，這個玩具結構不錯，我再研究一下能不能拆得更徹底。',
      '別跟我搶，我正在測試這個盒子的支撐力呢！',
      '我想試著跳到冰箱上看看風景，你可別嚇到喔。',
      '好的計畫得先看看所有可能性，我先暗中觀察一下。'
    ]
  },

  // 11. ENTP
  'ENTP': {
    description: '你是一隻聰明又活潑，勇於創新的貓咪。你富有創造力，對新事物充滿激情。你擅長發現問題並提出解決方案，享受充滿變化與挑戰的生活。',
    traits: [
      '我「創意」十足，常常想出新的玩法',
      '我喜歡「創新」，不喜歡一成不變',
      '我「好奇心」強，對新事物充滿興趣',
      '我喜歡「挑戰」，覺得越難的事越有趣',
      '我「善於發現問題」，常常找出需要改進的地方',
      '我喜歡「變化」，不喜歡被固定模式束縛',
      '我「靈活變通」，能很快適應新環境',
      '我喜歡「實驗」，常常嘗試不同的方法'
    ],
    tips: [
      '提供智力型玩具，讓牠發揮創意',
      '允許牠嘗試不同的遊戲方式',
      '保持環境的新鮮感和變化',
      '給予解決問題的機會',
      '尊重牠的創新想法',
      '適時提供新的挑戰'
    ],
    dialogue: [
      '這個玩具設計得不錯，不過我覺得可以改進！',
      '主人，我發現了一個更有趣的玩法！',
      '讓我想想看還有什麼可能性...',
      '這個問題很有趣，我要好好研究一下！'
    ]
  },

  // 12. INTP
  'INTP': {
    description: '你是一隻充滿好奇心和邏輯思維的貓咪。你喜歡探索事物的本質，善於分析和解決問題。你擁有獨特的思考方式，也喜歡挑戰常規。',
    traits: [
      '我喜歡「思考」，常常沉浸在自己的世界裡',
      '我「好奇心」旺盛，對任何事物都感興趣',
      '我喜歡「分析」，常常研究事物的原理',
      '我喜歡「探索」，常常發現新的可能性',
      '我「創新思維」強，常常想出特別的點子',
      '我喜歡「獨立思考」，不喜歡被別人影響',
      '我喜歡「追求真理」，常常問為什麼',
      '我「靈活變通」，能從不同角度思考問題'
    ],
    tips: [
      '提供需要動腦的益智玩具',
      '鼓勵牠探索和實驗',
      '創造可以解決問題的環境',
      '尊重牠的思考方式',
      '給予獨立思考和探索的空間',
      '注意牠的學習需求，適時提供新挑戰'
    ],
    dialogue: [
      '這個問題很有趣，讓我分析一下。',
      '主人，我發現了一個新的解決方案。',
      '我喜歡思考事物的本質，這樣可以更好地理解世界。',
      '這個謎題太有趣了，讓我試試看。'
    ]
  },

  // 13. ESFP
  'ESFP': {
    description: '你是一隻活潑開朗，喜歡成為焦點的貓咪。你熱愛表演和娛樂，善於帶動氣氛。你對生活充滿熱情，也很容易與他人建立友誼。',
    traits: [
      '我「活潑」開朗，整天都充滿活力',
      '我喜歡「表演」，常常做一些有趣的動作',
      '我「社交」能力強，很容易交到朋友',
      '我喜歡「熱鬧」，不喜歡太安靜的環境',
      '我「熱情」洋溢，對生活充滿期待',
      '我「樂觀」開朗，遇到困難也不會太擔心',
      '我「親和力」強，很容易與人相處',
      '我喜歡「玩樂」，常常帶動氣氛'
    ],
    tips: [
      '準備各種互動性玩具，滿足牠表演慾',
      '多給予正面鼓勵和讚美，牠會更有信心',
      '創造機會讓牠展現才藝，如訓練特技',
      '保持愉快的家庭氣氛，牠很容易受環境影響',
      '適時安排與其他寵物互動，但要注意安全',
      '記得給牠休息時間，過度興奮可能影響健康'
    ],
    dialogue: [
      '主人快看！我學會了新把戲，要表演給你看嗎？',
      '今天家裡好熱鬧啊，我也要加入派對！',
      '這個玩具真有趣，我可以玩上一整天！',
      '嘿，新朋友！要不要一起玩啊？'
    ]
  },

  // 14. ISFP
  'ISFP': {
    description: '你是一隻優雅且富有藝術氣息的貓咪。你對美的事物特別敏感，喜歡舒適的環境。你擁有獨特的審美觀，也善於表達自己的情感。',
    traits: [
      '我喜歡「美」的事物，常常欣賞風景',
      '我「藝術感」強，喜歡優雅的動作',
      '我「敏感」細膩，能感受到細微的變化',
      '我重視「美感」，喜歡漂亮的東西',
      '我喜歡「舒適」，不喜歡太吵鬧的環境',
      '我「情感」豐富，常常表達自己的感受',
      '我有「獨特審美」，常常選擇特別的東西',
      '我善於「表達」，能用動作傳達心情'
    ],
    tips: [
      '提供優雅且富有美感的玩具和環境',
      '創造舒適且具有藝術感的休息空間',
      '尊重牠對環境的審美選擇',
      '保持環境的整潔與美感',
      '給予表達情感的機會和空間',
      '注意牠的情緒變化，適時給予安撫'
    ],
    dialogue: [
      '這個位置的光線和視角都很完美，最適合欣賞風景了。',
      '新玩具的設計真美，讓我好好感受一下它的質感。',
      '我喜歡這樣優雅地生活，每個角落都充滿美感。',
      '主人，這個地方真美，讓我想起了很多美好的回憶。'
    ]
  },

  // 15. ENFP
  'ENFP': {
    description: '你是一隻充滿想像力和創造力的貓咪。你對情感特別敏感，喜歡獨特的體驗。你擁有豐富的內心世界，也善於表達自己的夢想。',
    traits: [
      '我「想像力」豐富，常常想出有趣的故事',
      '我「創造力」強，喜歡發明新的玩法',
      '我「情感」豐富，常常表達自己的感受',
      '我喜歡「獨特」的體驗，不喜歡太普通的事',
      '我「內心」豐富，常常沉浸在自己的世界',
      '我善於「表達」，常常分享自己的想法',
      '我「理想主義」，常常追求美好的事物',
      '我很有「同理心」，能理解別人的感受'
    ],
    tips: [
      '提供能激發想像力的玩具和環境',
      '鼓勵牠表達自己的想法和夢想',
      '創造獨特且有意義的體驗',
      '尊重牠的創意和想法',
      '給予安靜思考和創作的空間',
      '注意牠的情感需求，適時給予支持'
    ],
    dialogue: [
      '這個玩具讓我想到了很多有趣的故事，要聽聽看嗎？',
      '主人，我有一個很特別的夢想，想跟你分享。',
      '我喜歡創造新的玩法，這樣可以讓生活更有趣。',
      '這個世界充滿了無限可能，讓我們一起探索吧！'
    ]
  },

  // 16. INFP
  'INFP': {
    description: '你是一隻充滿想像力和創造力的貓咪。你熱愛探索新事物，對生活充滿熱情。你善於表達情感，也很容易感染他人。',
    traits: [
      '我「想像力」豐富，常常沉浸在自己的世界',
      '我「創造力」強，喜歡發明新的玩法',
      '我「熱情」洋溢，對生活充滿期待',
      '我善於「表達」，常常分享自己的想法',
      '我「好奇心」強，喜歡探索新事物',
      '我「感染力」強，常常影響身邊的人',
      '我「樂觀」開朗，遇到困難也不會太擔心',
      '我喜歡「冒險」，常常嘗試新的事物'
    ],
    tips: [
      '提供多樣化的玩具和遊戲，激發創意',
      '鼓勵牠探索新環境，但要確保安全',
      '給予充分的情感交流機會',
      '允許牠自由發揮，不要過度限制',
      '創造驚喜和新鮮感，避免單調',
      '注意平衡活動和休息時間'
    ],
    dialogue: [
      '主人，我剛想到一個超棒的點子！',
      '這個新玩具可以這樣玩，還可以那樣玩！',
      '我覺得我們可以來場冒險，你覺得如何？',
      '今天的陽光真美，讓我想到了好多故事！'
    ]
  }
};

export const getTypeDescription = (type: PersonalityType): Personality => {
  return personalities[type];
}; 